import { useInfiniteQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import { Controls, ControlsButton } from '@/components/Controls';
import DateRangeFilter from '@/components/DateRangeFilter';
import ExportButton from '@/components/ExportButton';
import FullScreenButton from '@/components/FullScreenButton';
import InfiniteLoader from '@/components/InfiniteLoader';
import { PageHeading, SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import {
  SortableTh,
  Table,
  TableContent,
  TableHeader,
  Th,
} from '@/components/Table';
import Text from '@/components/Text';
import ViewOptionsModal from '@/components/ViewOptionsModal';
import useHasPermission from '@/hooks/useHasPermission';
import InvoicesSearchBar from '@/pages/InvoiceList/components/InvoicesSearchBar';
import { PaginatedResponse, SortState } from '@/types/resources';
import { FreelancerDetailsResource } from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';
import { RootState } from '@/utils/store';

import FreelancerFormModal from './FreelancerFormModal';
import FreelancersFilter from './FreelancersFilter';
import FreelancersNotifications from './FreelancersNotifications';
import FreelancersSwitch from './FreelancersSwitch';
import FreelancersTableRow from './FreelancersTableRow';

enum Tabs {
  Employed = 'employed',
  Unemployed = 'unemployed',
  All = 'all',
}

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.ASC,
  property: 'are_fields_prefilled',
};

const columns = [
  {
    title: 'Order Nr.',
    toggable: false,
    defaultVisibility: true,
  },
  {
    title: 'Name Surname',
    toggable: false,
    defaultVisibility: true,
  },
  {
    title: 'Status',
    toggable: false,
    defaultVisibility: true,
  },
  {
    title: 'Start Date',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Category',
    toggable: true,
    defaultVisibility: true,
    requiresHrAccess: true,
  },

  {
    title: 'Company',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Department',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Subunit',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Role',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Finance Account Number',
    toggable: true,
    defaultVisibility: true,
    requiresHrAccess: true,
  },
  {
    title: 'Employment Type',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Current Hour Wage',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Current Day Wage',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Current Month Wage',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Current Single Job Wage',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Current Other Wage',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Work Email',
    toggable: true,
    defaultVisibility: true,
    requiresHrAccess: true,
    workEmailAccess: true,
  },
  {
    title: 'Personal Code',
    toggable: true,
    defaultVisibility: true,
    requiresHrAccess: true,
  },
  {
    title: 'Gender',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Birthday Date',
    toggable: true,
    defaultVisibility: true,
    requiresHrAccess: true,
  },
  {
    title: 'Phone Number',
    toggable: true,
    defaultVisibility: true,
    requiresHrAccess: true,
  },
  {
    title: 'Personal Email',
    toggable: true,
    defaultVisibility: true,
    requiresHrAccess: true,
    workEmailAccess: true,
  },
  {
    title: 'Address',
    toggable: true,
    defaultVisibility: true,
    requiresHrAccess: true,
  },
  {
    title: 'Created By',
    toggable: true,
    defaultVisibility: true,
    requiresHrAccess: true,
  },
  {
    title: 'Last Edited',
    toggable: true,
    defaultVisibility: true,
    requiresHrAccess: true,
  },
];

export const HeadingsRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0;
`;

const ButtonsHolder = styled.div`
  display: flex;
  margin-left: auto;
  gap: 8px;
`;

const StyledTableHeader = styled(TableHeader)`
  position: relative;
`;

const TableSubHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const StyledTable = styled(Table)<{ $seeActionColumn?: boolean }>`
  border-collapse: separate;
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    min-width: 120px;
    &:nth-child(1) {
      width: 84px;
      min-width: 84px;
      padding-left: 24px;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 0;
      top: 0;
      bottom: 0;
    }
    &:nth-child(2) {
      width: 150px;
      min-width: 150px;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 84px;
      top: 0;
      bottom: 0;
    }
    ${({ $seeActionColumn }) =>
      $seeActionColumn &&
      css`
        &:last-child {
          min-width: 56px;
          width: 56px;
          z-index: 1;
          position: relative;
        }
      `}
  }
  td {
    padding-top: 26px;
    padding-bottom: 26px;
    ${({ $seeActionColumn }) =>
      $seeActionColumn &&
      css`
        &:last-child {
          min-width: 56px;
          width: 56px;
          position: sticky;
          right: 0;
          top: 0;
          bottom: 0;
          z-index: 1;
          padding-right: 16px;
          padding-left: 16px;
          border-left: 1px solid ${({ theme }) => theme.colors.grayscale} !important;
        }
      `}
  }
`;

const MembersCount = styled(Text)`
  background: #909090;
  border-radius: 9px;
  height: 18px;
  display: flex;
  padding: 0 10px;
  min-width: 24px;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
`;

const MembersCountHolder = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;
`;

const FreelancersTable = () => {
  const filterOptions = useSelector(
    (rootState: RootState) => rootState.filters.filters.freelancerFilters,
  );

  const userCanSeeData = useHasPermission('view all freelancers');

  const userCanManageData = useHasPermission('manage freelancers');

  const userCanManageWorkEmail = useHasPermission(
    'manage freelancer work email',
  );

  const userCanOnlyManageWorkEmail = userCanManageWorkEmail && !userCanSeeData;

  const visibleColumns = useMemo(() => {
    if (userCanOnlyManageWorkEmail) {
      return columns.filter((col) => !!col.workEmailAccess);
    }
    return columns.filter((col) => !col.requiresHrAccess || userCanSeeData);
  }, [userCanSeeData, userCanOnlyManageWorkEmail]);

  const [showFreelancerModal, setShowFreelancerModal] = useState(false);

  const [selectedDateRange, setSelectedDateRange] = useState<{
    rangeStart?: DateTime;
    rangeEnd?: DateTime;
  }>({});

  const [selectedStartedAtDateRange, setSelectedStartedAtDateRange] = useState<{
    rangeStart?: DateTime;
    rangeEnd?: DateTime;
  }>({});

  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const [sortExpiringContracts, setSortExpiringContracts] = useState(false);

  const [search, setSearch] = useState('');

  const { type } = useParams();

  const tableViewOptions = useSelector(
    (rootState: RootState) => rootState.options.tableViews['freelancersTable'],
  );

  const params = {
    ...(!!search && {
      'filter[search]': search,
    }),
    ...(type !== Tabs.All && {
      'filter[status]': type,
    }),
    'filter[insurance]': filterOptions?.insurance,
    'filter[isBackboneUser]': filterOptions?.isBackboneUser,
    ...(filterOptions?.departments && {
      'filter[department_id]': filterOptions.departments,
    }),
    ...(filterOptions?.roles && {
      'filter[role_id]': filterOptions.roles,
    }),
    ...(filterOptions?.categories && {
      'filter[category]': filterOptions.categories,
    }),
    ...(filterOptions?.companies && {
      'filter[company_id]': filterOptions.companies,
    }),
    ...(filterOptions?.subunits && {
      'filter[subunit_id]': filterOptions.subunits,
    }),
    ...(filterOptions?.team_leads && {
      'filter[team_leads]': filterOptions.team_leads,
    }),
    ...(filterOptions?.employment_types && {
      'filter[employment_types]': filterOptions.employment_types,
    }),
    ...(filterOptions?.countries && {
      'filter[countries]': filterOptions.countries,
    }),
    ...(filterOptions?.gender && {
      'filter[gender]': filterOptions.gender,
    }),
    ...(selectedDateRange.rangeStart &&
      !selectedDateRange.rangeEnd && {
        'filter[birthday_exact_date]':
          selectedDateRange.rangeStart.toFormat('MM-dd'),
      }),
    ...(selectedDateRange.rangeStart &&
      selectedDateRange.rangeEnd && {
        'filter[birthday_date_from]':
          selectedDateRange.rangeStart.toFormat('MM-dd'),
        'filter[birthday_date_to]':
          selectedDateRange.rangeEnd.toFormat('MM-dd'),
      }),
    ...(selectedStartedAtDateRange.rangeStart &&
      !selectedStartedAtDateRange.rangeEnd && {
        'filter[started_at_exact_date]':
          selectedStartedAtDateRange.rangeStart.toFormat('yyyy-MM-dd'),
      }),
    ...(selectedStartedAtDateRange.rangeStart &&
      selectedStartedAtDateRange.rangeEnd && {
        'filter[started_at_date_from]':
          selectedStartedAtDateRange.rangeStart.toFormat('yyyy-MM-dd'),
        'filter[started_at_date_to]':
          selectedStartedAtDateRange.rangeEnd.toFormat('yyyy-MM-dd'),
      }),
    ...(type === Tabs.Employed &&
      sortExpiringContracts && {
        'filter[expiring_contracts]': true,
      }),
    ...(sort.direction !== 'DEFAULT' && {
      sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
    }),
    ...(type === Tabs.Employed &&
      sortExpiringContracts && {
        sort: `${sort.direction === 'DESC' ? '-' : ''}${'contract_ends'}`,
      }),
  };

  const fetchCsv = () =>
    api.get<never, string>(`/v1/payments/freelancers-data-export`, {
      params,
    });

  const fetchFreelancers = ({ pageParam = '/v1/payments/freelancers' }) => {
    return api.get<never, PaginatedResponse<FreelancerDetailsResource>>(
      pageParam,
      {
        params,
      },
    );
  };

  const {
    data,
    isError,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<FreelancerDetailsResource>>(
    [
      'freelancers_details',
      [
        sort,
        search,
        type,
        filterOptions,
        sortExpiringContracts,
        selectedDateRange,
        selectedStartedAtDateRange,
      ],
    ],
    fetchFreelancers,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    },
  );

  const freelancers = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<FreelancerDetailsResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  return (
    <>
      <HeadingsRow>
        <PageHeading>Freelance List</PageHeading>
        {userCanManageData && (
          <Button onClick={() => setShowFreelancerModal(true)}>Add new</Button>
        )}
      </HeadingsRow>
      <StyledTableHeader>
        <Controls>
          <NavLink
            to={`/general/freelancers/type/employed`}
            children={({ isActive }) => (
              <ControlsButton active={isActive}>Employed</ControlsButton>
            )}
          />
          <NavLink
            to={`/general/freelancers/type/unemployed`}
            children={({ isActive }) => (
              <ControlsButton active={isActive}>Unemployed</ControlsButton>
            )}
          />
          <NavLink
            to={`/general/freelancers/type/all`}
            children={({ isActive }) => (
              <ControlsButton active={isActive}>All</ControlsButton>
            )}
          />
        </Controls>
        <ButtonsHolder>
          {userCanSeeData && <FreelancersNotifications />}
          <DateRangeFilter
            selectedDateRange={selectedStartedAtDateRange}
            setSelectedDateRange={(range) => {
              setSelectedStartedAtDateRange(range);
            }}
            text="Start Date"
            allDaysSelectable
          />
          <DateRangeFilter
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={(range) => {
              setSelectedDateRange(range);
            }}
            text="Birthday Date"
            allDaysSelectable
          />
          {userCanSeeData && (
            <ExportButton
              fetchCsv={fetchCsv}
              fileName={`freelancers-data-export.csv`}
            />
          )}
          {userCanSeeData && <FreelancersFilter />}
          <ViewOptionsModal
            columns={visibleColumns}
            tableKey="freelancersTable"
          />
          <FullScreenButton />
        </ButtonsHolder>
      </StyledTableHeader>
      <InvoicesSearchBar
        setValue={setSearch}
        placeholder="Search by name and email"
      />
      <TableSubHeader>
        <MembersCountHolder>
          <Text $type="smallHeadline" $color="gray">
            LIST OF MEMBERS
          </Text>
          <MembersCount $type="extraSmall" $color="white">
            {!!data?.pages[0]?.meta?.total ? data?.pages[0].meta.total : '0'}
          </MembersCount>
        </MembersCountHolder>
        {userCanSeeData && type === Tabs.Employed && (
          <FreelancersSwitch
            isActive={sortExpiringContracts}
            onClick={() => setSortExpiringContracts((prev) => !prev)}
          />
        )}
      </TableSubHeader>
      <ScrollableTable>
        <StyledTable
          $seeActionColumn={userCanManageData || userCanManageWorkEmail}
        >
          <thead>
            <tr>
              <Th>Order Nr.</Th>
              <SortableTh
                property="receiver"
                title="Name Surname"
                setSort={setSort}
                sort={sort}
              />
              {userCanSeeData && (
                <SortableTh
                  property="created_at"
                  title="Date Created"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {!userCanOnlyManageWorkEmail &&
                tableViewOptions['Start Date'] && <Th>Start Date</Th>}
              {!userCanOnlyManageWorkEmail && <Th>Status</Th>}
              {userCanSeeData && tableViewOptions.Category && (
                <SortableTh
                  property="category"
                  title="Category"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {!userCanOnlyManageWorkEmail && tableViewOptions.Company && (
                <Th>Company</Th>
              )}
              {!userCanOnlyManageWorkEmail && tableViewOptions.Department && (
                <Th>Department</Th>
              )}
              {!userCanOnlyManageWorkEmail && tableViewOptions.Subunit && (
                <Th>Subunit</Th>
              )}
              {!userCanOnlyManageWorkEmail && tableViewOptions.Role && (
                <Th>Role</Th>
              )}
              {userCanSeeData && tableViewOptions['Finance Account Number'] && (
                <Th>Finance Account Number</Th>
              )}
              {!userCanOnlyManageWorkEmail &&
                tableViewOptions['Employment Type'] && <Th>Employment Type</Th>}
              {!userCanOnlyManageWorkEmail &&
                tableViewOptions['Current Hour Wage'] && (
                  <Th>Current Hour Wage</Th>
                )}
              {!userCanOnlyManageWorkEmail &&
                tableViewOptions['Current Day Wage'] && (
                  <Th>Current Day Wage</Th>
                )}
              {!userCanOnlyManageWorkEmail &&
                tableViewOptions['Current Month Wage'] && (
                  <Th>Current Month Wage</Th>
                )}
              {!userCanOnlyManageWorkEmail &&
                tableViewOptions['Current Single Job Wage'] && (
                  <Th>Current Single Job Wage</Th>
                )}
              {!userCanOnlyManageWorkEmail &&
                tableViewOptions['Current Other Wage'] && (
                  <Th>Current Other Wage</Th>
                )}
              {(userCanSeeData || userCanManageWorkEmail) &&
                tableViewOptions['Work Email'] && <Th>Work Email</Th>}
              {userCanSeeData && tableViewOptions['Personal Code'] && (
                <Th>Personal Code</Th>
              )}
              {!userCanOnlyManageWorkEmail && tableViewOptions['Gender'] && (
                <Th>Gender</Th>
              )}
              {userCanSeeData && tableViewOptions['Birthday Date'] && (
                <SortableTh
                  property="date_of_birth"
                  title="Birthday Date"
                  setSort={setSort}
                  sort={sort}
                />
              )}
              {userCanSeeData && tableViewOptions['Phone Number'] && (
                <Th>Phone Number</Th>
              )}
              {(userCanSeeData || userCanManageWorkEmail) &&
                tableViewOptions['Personal Email'] && <Th>Personal Email</Th>}
              {userCanSeeData && tableViewOptions.Address && <Th>Address</Th>}
              {userCanSeeData && tableViewOptions['Created By'] && (
                <Th>Created By</Th>
              )}
              {userCanSeeData && tableViewOptions['Last Edited'] && (
                <Th>Last Edited</Th>
              )}
              {(userCanManageData || userCanManageWorkEmail) && (
                <Th>
                  <SrOnly>Actions</SrOnly>
                </Th>
              )}
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!freelancers?.length}
            >
              {freelancers?.map((freelancer, index) => (
                <FreelancersTableRow
                  key={freelancer.id}
                  freelancer={freelancer}
                  index={index + 1}
                  sortExpiringContracts={sortExpiringContracts}
                  userCanOnlyManageWorkEmail={userCanOnlyManageWorkEmail}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <AnimatePresence>
        {userCanManageData && showFreelancerModal && (
          <FreelancerFormModal onClose={() => setShowFreelancerModal(false)} />
        )}
      </AnimatePresence>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default FreelancersTable;
